import { SettingsTabType } from "@/components/settings/SettingsTabType";
import { inject } from "regexparam";
import {
  COMPLIANCE_TASKS_TASK_ID,
  COMPLIANCE_TRACKING_TRACKING_ID,
  ENERGY_MANAGEMENT_BILLING_ACCOUNTS_BILLING_ACCOUNT_ID,
  ENERGY_MANAGEMENT_INITIATIVES_INITIATIVE_ID,
  ENERGY_MANAGEMENT_LOCATIONS_PROPERTY_ID,
  ENERGY_MANAGEMENT_LOCATIONS_PROPERTY_ID_EDIT,
  ENERGY_MANAGEMENT_METERS_METER_ID,
  ENERGY_MANAGEMENT_METERS_METER_ID_EDIT,
  ENERGY_MANAGEMENT_SERVICE_ACCOUNTS_SERVICE_ACCOUNT_ID,
  ENERGY_MANAGEMENT_STATEMENTS_STATEMENT_ID,
  ENERGY_MANAGEMENT_UTILITIES_ORG_VENDOR_ID,
  EXPLORE_DASHBOARD_DASHBOARD_ID,
  EXPLORE_REPORTS_EDIT,
  EXPLORE_REPORTS_REPORT_ID,
  FILE_ID,
  JOBS_JOB_ID,
  NOTIFICATIONS_NOTIFICATION_ID,
  PROGRAMS_PROGRAMS_PROGRAM_ID,
  PROGRAMS_PUBLIC_PROGRAM_ID,
  PROGRAMS_PROGRAMS_PROGRAM_ID_CRITERIA,
  PROGRAMS_PROGRAMS_PROGRAM_ID_CYCLES,
  PROGRAMS_PROGRAMS_PROGRAM_ID_CYCLES_CYCLE_ID,
  PROGRAMS_PROGRAMS_PROGRAM_ID_CYCLES_CYCLE_ID_COMPLIANCE,
  PROGRAMS_PROGRAMS_PROGRAM_ID_CYCLES_CYCLE_ID_ENROLLMENTS,
  PROGRAMS_PROGRAMS_PROGRAM_ID_CYCLES_CYCLE_ID_FORMS,
  PROGRAMS_PROGRAMS_PROGRAM_ID_CYCLES_CYCLE_ID_FORMS_FORM_ID_EDIT,
  PROGRAMS_PROGRAMS_PROGRAM_ID_CYCLES_CYCLE_ID_FORMS_FORM_ID_PREVIEW,
  PROGRAMS_PROGRAMS_PROGRAM_ID_CYCLES_CYCLE_ID_FORMS_NEW,
  PROGRAMS_PROGRAMS_PROGRAM_ID_CYCLES_CYCLE_ID_PORTALS,
  PROGRAMS_PROGRAMS_PROGRAM_ID_CYCLES_CYCLE_ID_PORTALS_NEW,
  PROGRAMS_PROGRAMS_PROGRAM_ID_CYCLES_CYCLE_ID_PORTALS_PORTAL_ID_EDIT,
  PROGRAMS_PROGRAMS_PROGRAM_ID_CYCLES_CYCLE_ID_PORTALS_PORTAL_ID_PREVIEW,
  PROGRAMS_PROGRAMS_PROGRAM_ID_CYCLES_CYCLE_ID_SUBMISSIONS,
  PROGRAMS_PROGRAMS_PROGRAM_ID_CYCLES_NEW,
  PROGRAMS_PROGRAMS_PROGRAM_ID_DEADLINES,
  PROGRAMS_PROGRAMS_PROGRAM_ID_EDIT,
  PROGRAMS_PROGRAMS_PROGRAM_ID_EMISSIONS,
  PUBLIC_DASHBOARD_DASHBOARD_ID,
  PUBLIC_REPORT_REPORT_ID,
  SETTINGS_TAB_NAME,
} from "./const";

// Utils
export const asFullUrl = (relativeUrl: string) => {
  const { protocol, host } = window.location;

  if (relativeUrl.startsWith("/") || host.endsWith("/")) {
    return `${protocol}//${host}${relativeUrl}`;
  } else {
    return `${protocol}//${host}/${relativeUrl}`;
  }
};

export const publicReportUrl = (reportId: string) =>
  inject(PUBLIC_REPORT_REPORT_ID, { reportId });

export const publicDashboardUrl = (dashboardId: string) =>
  inject(PUBLIC_DASHBOARD_DASHBOARD_ID, { dashboardId });

export const settingsUrl = (tabName: SettingsTabType) =>
  inject(SETTINGS_TAB_NAME, { tabName });

export const jobUrl = (jobId: string) => inject(JOBS_JOB_ID, { jobId });
export const fileUrl = (id: string) => inject(FILE_ID, { id });
export const notificationUrl = (notificationId: string) =>
  inject(NOTIFICATIONS_NOTIFICATION_ID, { notificationId });
export const energyManagementDashboardUrl = (dashboardId: string) =>
  inject(EXPLORE_DASHBOARD_DASHBOARD_ID, { dashboardId });
export const energyManagementInitiativeUrl = (initiativeId: string) =>
  inject(ENERGY_MANAGEMENT_INITIATIVES_INITIATIVE_ID, { initiativeId });
export const energyManagementLocationUrl = (propertyId: string) =>
  inject(ENERGY_MANAGEMENT_LOCATIONS_PROPERTY_ID, { propertyId });
export const energyManagementLocationEditUrl = (propertyId: string) =>
  inject(ENERGY_MANAGEMENT_LOCATIONS_PROPERTY_ID_EDIT, { propertyId });
export const energyManagementReportUrl = (reportId: string) =>
  inject(EXPLORE_REPORTS_REPORT_ID, { reportId });
export const energyManagementReportEditUrl = (reportId: string) =>
  inject(EXPLORE_REPORTS_EDIT, { reportId });
export const energyManagementStatementUrl = (statementId: string) =>
  inject(ENERGY_MANAGEMENT_STATEMENTS_STATEMENT_ID, { statementId });
export const energyManagementUtilityUrl = (orgVendorId: string) =>
  inject(ENERGY_MANAGEMENT_UTILITIES_ORG_VENDOR_ID, { orgVendorId });
export const energyManagementBillingAccountUrl = (billingAccountId: string) =>
  inject(ENERGY_MANAGEMENT_BILLING_ACCOUNTS_BILLING_ACCOUNT_ID, {
    billingAccountId,
  });
export const energyManagementServiceAccountUrl = (serviceAccountId: string) =>
  inject(ENERGY_MANAGEMENT_SERVICE_ACCOUNTS_SERVICE_ACCOUNT_ID, {
    serviceAccountId,
  });
export const energyManagementMeterUrl = (meterId: string) =>
  inject(ENERGY_MANAGEMENT_METERS_METER_ID, { meterId });
export const energyManagementMeterEditUrl = (meterId: string) =>
  inject(ENERGY_MANAGEMENT_METERS_METER_ID_EDIT, { meterId });
export const programUrl = (programId: string) =>
  inject(PROGRAMS_PROGRAMS_PROGRAM_ID, { programId });
export const programCriteriaUrl = (programId: string) =>
  inject(PROGRAMS_PROGRAMS_PROGRAM_ID_CRITERIA, { programId });
export const programEditUrl = (programId: string) =>
  inject(PROGRAMS_PROGRAMS_PROGRAM_ID_EDIT, { programId });
export const programDeadlinesUrl = (programId: string) =>
  inject(PROGRAMS_PROGRAMS_PROGRAM_ID_DEADLINES, { programId });
export const programEmissionsUrl = (programId: string) =>
  inject(PROGRAMS_PROGRAMS_PROGRAM_ID_EMISSIONS, { programId });
export const programCyclesUrl = (programId: string) =>
  inject(PROGRAMS_PROGRAMS_PROGRAM_ID_CYCLES, { programId });
export const programCycleNewUrl = (programId: string) =>
  inject(PROGRAMS_PROGRAMS_PROGRAM_ID_CYCLES_NEW, { programId });
export const programCycleUrl = (programId: string, cycleId: string) =>
  inject(PROGRAMS_PROGRAMS_PROGRAM_ID_CYCLES_CYCLE_ID, { programId, cycleId });
export const programCycleComplianceUrl = (programId: string, cycleId: string) =>
  inject(PROGRAMS_PROGRAMS_PROGRAM_ID_CYCLES_CYCLE_ID_COMPLIANCE, {
    programId,
    cycleId,
  });
export const programCycleEnrollmentsUrl = (
  programId: string,
  cycleId: string,
) =>
  inject(PROGRAMS_PROGRAMS_PROGRAM_ID_CYCLES_CYCLE_ID_ENROLLMENTS, {
    programId,
    cycleId,
  });
export const programCycleSubmissionsUrl = (
  programId: string,
  cycleId: string,
) =>
  inject(PROGRAMS_PROGRAMS_PROGRAM_ID_CYCLES_CYCLE_ID_SUBMISSIONS, {
    programId,
    cycleId,
  });
export const programCycleFormsUrl = (programId: string, cycleId: string) =>
  inject(PROGRAMS_PROGRAMS_PROGRAM_ID_CYCLES_CYCLE_ID_FORMS, {
    programId,
    cycleId,
  });
export const programCycleFormNewUrl = (programId: string, cycleId: string) =>
  inject(PROGRAMS_PROGRAMS_PROGRAM_ID_CYCLES_CYCLE_ID_FORMS_NEW, {
    programId,
    cycleId,
  });
export const programCycleFormEditUrl = (
  programId: string,
  cycleId: string,
  formId: string,
) =>
  inject(PROGRAMS_PROGRAMS_PROGRAM_ID_CYCLES_CYCLE_ID_FORMS_FORM_ID_EDIT, {
    programId,
    cycleId,
    formId,
  });
export const programCycleFormPreviewUrl = (
  programId: string,
  cycleId: string,
  formId: string,
) =>
  inject(PROGRAMS_PROGRAMS_PROGRAM_ID_CYCLES_CYCLE_ID_FORMS_FORM_ID_PREVIEW, {
    programId,
    cycleId,
    formId,
  });
export const programCyclePortalsUrl = (programId: string, cycleId: string) =>
  inject(PROGRAMS_PROGRAMS_PROGRAM_ID_CYCLES_CYCLE_ID_PORTALS, {
    programId,
    cycleId,
  });
export const programCyclePortalNewUrl = (programId: string, cycleId: string) =>
  inject(PROGRAMS_PROGRAMS_PROGRAM_ID_CYCLES_CYCLE_ID_PORTALS_NEW, {
    programId,
    cycleId,
  });
export const programCyclePortalEditUrl = (
  programId: string,
  cycleId: string,
  portalId: string,
) =>
  inject(PROGRAMS_PROGRAMS_PROGRAM_ID_CYCLES_CYCLE_ID_PORTALS_PORTAL_ID_EDIT, {
    programId,
    cycleId,
    portalId,
  });
export const programCyclePortalPreviewUrl = (
  programId: string,
  cycleId: string,
  portalId: string,
) =>
  inject(
    PROGRAMS_PROGRAMS_PROGRAM_ID_CYCLES_CYCLE_ID_PORTALS_PORTAL_ID_PREVIEW,
    {
      programId,
      cycleId,
      portalId,
    },
  );
export const complianceTaskUrl = (taskId: string) =>
  inject(COMPLIANCE_TASKS_TASK_ID, { taskId });
export const complianceTrackingUrl = (trackingId: string) =>
  inject(COMPLIANCE_TRACKING_TRACKING_ID, { trackingId });

export const publicProgramUrl = (programId: string) =>
  inject(PROGRAMS_PUBLIC_PROGRAM_ID, { programId });
